<template>
  <v-card data-testid="signup_page_edit">
    <v-toolbar grey flat>
      <v-btn icon @click="$emit('close')" data-testid="signup_page_edit_close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>{{ is_new ? $t("admin_signup_pages.create") : $t("signup_page_edit.edit") }}</v-toolbar-title>
    </v-toolbar>
    <v-card-text>
      <v-text-field
        v-model="local_page.title"
        outlined
        :label="$t('common.title')"
        data-testid="signup_page_edit_title"
      />
      <v-textarea
        v-model="local_page.instructions"
        outlined
        :label="$t('common.instructions')"
        data-testid="signup_page_edit_instructions"
      />
      <v-autocomplete
        v-model="local_page.clinic_id"
        :items="clinic_options"
        :label="$t('signup_page_edit.clinic')"
        outlined
        data-testid="signup_page_edit_clinic"
      />

      <h3 class="mt-4 mb-2">{{ $t("signup_page_edit.clinic_tags") }}</h3>
      <v-textarea
        v-model="tags_text"
        outlined
        :label="$t('signup_page_edit.clinic_tags')"
        :hint="$t('signup_page_edit.clinic_tags_hint')"
        persistent-hint
        data-testid="signup_page_edit_tags"
      />

      <h3 class="mt-6 mb-2">{{ $t("signup_page_edit.custom_fields") }}</h3>
      <VuetifySurveyEditor
        v-model="local_page.survey"
        :allow_edit_title="false"
        :allow_edit_instructions="false"
        :show_item_id="true"
        :add_item_text="$t('signup_page_edit.add_field')"
      />

      <template v-if="local_page.clinic_id">
        <h3 class="mt-6 mb-2">{{ $t("signup_page_edit.auto_share") }}</h3>
        <v-layout wrap>
          <v-flex xs6 class="pr-2">
            <v-select
              v-model="source_filter.entity_type"
              :items="entity_type_options"
              :label="$t('signup_page_edit.entity_type')"
              clearable
              outlined
              dense
              data-testid="signup_page_edit_source_entity_type"
              @change="load_sources"
            />
          </v-flex>
          <v-flex xs6>
            <v-text-field
              v-model="source_filter.search"
              :label="$t('common.search')"
              outlined
              dense
              data-testid="signup_page_edit_source_search"
              @keyup.enter="load_sources"
              @blur="load_sources"
            />
          </v-flex>
        </v-layout>

        <v-data-table
          :items="source_items"
          :headers="source_headers"
          :items-per-page="-1"
          hide-default-footer
          dense
          data-testid="signup_page_edit_source_table"
        >
          <template v-slot:item.add="{ item }">
            <v-btn
              small
              :disabled="is_source_selected(item)"
              @click="add_source(item)"
              data-testid="signup_page_edit_add_source"
            >
              {{ $t("signup_page_edit.add") }}
            </v-btn>
          </template>
        </v-data-table>

        <v-chip
          v-for="source in local_page.auto_share_sources"
          :key="source.entity_type + source.id"
          class="ma-1"
          close
          data-testid="signup_page_edit_selected_source"
          @click:close="remove_source(source)"
        >
          {{ source_label(source) }}
        </v-chip>
      </template>

      <v-alert v-if="error_message" type="error" outlined class="mt-4">{{ error_message }}</v-alert>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn text @click="$emit('close')" data-testid="signup_page_edit_cancel">{{ $t("common.cancel") }}</v-btn>
      <v-btn color="primary" :loading="saving" @click="save" data-testid="signup_page_edit_save">{{ $t("common.save") }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import axios from "axios";
import SignupPage from "../../classes/signup_page";
import VuetifySurveyEditor from "../vuetify-survey/components/VuetifySurveyEditor.vue";

export default {
  components: { VuetifySurveyEditor },
  props: {
    value: {
      type: Object,
      required: true,
    },
    clinics: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      local_page: SignupPage.fromJson(JSON.parse(JSON.stringify(this.value))),
      tags_text: (this.value.tags || []).join("\n"),
      saving: false,
      error_message: "",
      source_items: [],
      source_filter: {
        entity_type: null,
        search: "",
      },
      entity_type_options: [
        { text: this.$t("community_sharing.video_exercise"), value: "video_exercise" },
        { text: this.$t("community_sharing.grid_set"), value: "grid_set" },
        { text: this.$t("community_sharing.grid_schedule"), value: "grid_schedule" },
        { text: this.$t("community_sharing.grid_sequence"), value: "grid_sequence" },
        { text: this.$t("community_sharing.reading_text"), value: "reading_text" },
        { text: this.$t("community_sharing.printable_sheet"), value: "file" },
      ],
      source_headers: [
        { text: this.$t("common.title"), value: "title" },
        { text: this.$t("signup_page_edit.entity_type"), value: "entity_type" },
        { text: "", value: "add", sortable: false },
      ],
    };
  },
  computed: {
    is_new() {
      return !this.pages_existed();
    },
    clinic_options() {
      return this.clinics.map((clinic) => ({
        text: clinic.title,
        value: clinic.id,
      }));
    },
  },
  watch: {
    "local_page.clinic_id": {
      immediate: true,
      handler(clinic_id, previous_clinic_id) {
        if (previous_clinic_id && clinic_id !== previous_clinic_id) {
          this.local_page.auto_share_sources = [];
        }
        this.load_sources();
      },
    },
  },
  methods: {
    pages_existed() {
      return Boolean(this.value && this.value.created);
    },
    load_sources() {
      if (!this.local_page.clinic_id) {
        this.source_items = [];
        return;
      }
      const params = {};
      if (this.source_filter.entity_type) {
        params.entity_type = this.source_filter.entity_type;
      }
      if (this.source_filter.search) {
        params.search = this.source_filter.search;
      }
      axios.get("/api/signup_page/source_content/" + this.local_page.clinic_id, { params }).then((response) => {
        if (response.data.status == "error") {
          messageBus.$emit("api-error", response.data);
          return;
        }
        this.source_items = response.data.data.items;
      });
    },
    is_source_selected(item) {
      return this.local_page.auto_share_sources.some(
        (source) => source.entity_type == item.entity_type && source.id == item.id
      );
    },
    add_source(item) {
      if (this.is_source_selected(item)) {
        return;
      }
      this.local_page.auto_share_sources.push({
        entity_type: item.entity_type,
        id: item.id,
        title: item.title,
        clinic_title: item.clinic_title,
      });
    },
    remove_source(source) {
      this.local_page.auto_share_sources = this.local_page.auto_share_sources.filter(
        (item) => !(item.entity_type == source.entity_type && item.id == source.id)
      );
    },
    source_label(source) {
      if (source.title) {
        return source.entity_type + ": " + source.title;
      }
      const match = this.source_items.find((item) => item.entity_type == source.entity_type && item.id == source.id);
      if (match) {
        return source.entity_type + ": " + match.title;
      }
      return source.entity_type + " " + source.id;
    },
    tag_error() {
      const re = /^(?<tag_name>[a-z0-9_]+)(\((?<tag_value>.+)\))?$/;
      const tags = this.tags_text
        .split("\n")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);
      for (let tag of tags) {
        let match = tag.match(re);
        if (!match) {
          return this.$t("signup_page_edit.invalid_tag");
        }
        if (match.groups.tag_value) {
          try {
            JSON.parse(match.groups.tag_value);
          } catch (e) {
            return this.$t("signup_page_edit.invalid_tag");
          }
        }
      }
      return null;
    },
    save() {
      this.error_message = "";
      if (!this.local_page.title) {
        this.error_message = this.$t("signup_page_edit.title_required");
        return;
      }
      if (!this.local_page.clinic_id) {
        this.error_message = this.$t("signup_page_edit.clinic_required");
        return;
      }
      const tag_error = this.tag_error();
      if (tag_error) {
        this.error_message = tag_error;
        return;
      }
      this.local_page.tags = this.tags_text
        .split("\n")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);
      this.local_page.auto_share_sources = this.local_page.auto_share_sources.map((source) => ({
        entity_type: source.entity_type,
        id: source.id,
      }));

      this.saving = true;
      const request = this.pages_existed()
        ? axios.put("/api/signup_page/" + this.local_page.id, this.local_page)
        : axios.post("/api/signup_page", this.local_page);

      request.then((response) => {
        this.saving = false;
        if (response.data.status == "error") {
          messageBus.$emit("api-error", response.data);
          this.error_message = response.data.error_message;
          return;
        }
        messageBus.$emit("success", this.$t("signup_page_edit.saved"));
        this.$emit("saved");
      }).catch(() => {
        this.saving = false;
      });
    },
  },
};
</script>
